import React, { useState } from 'react';
import { Mail, ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: 'Book a Free Session', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject,
        message: form.message.trim()
      });
      toast.success(res.data.message);
      setSent(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const subjects = [
    'Book a Free Session',
    'Coaching Enquiry',
    'Ebook Question',
    'Workshop Interest',
    'General Enquiry'
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-cream">
      <Navbar />

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12">
            {/* Info */}
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full mb-6">
                <Mail className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Get In Touch</span>
              </div>
              <h1 className="font-heading text-4xl text-charcoal mb-5">Contact Janelle</h1>
              <p className="text-charcoal-light leading-relaxed mb-8">
                Ready to start your healing journey? Have a question about coaching, the ebook, or upcoming workshops? 
                Fill out the form and I'll get back to you personally.
              </p>
              
              <div className="space-y-6">
                <div className="bg-sage/5 rounded-2xl p-5">
                  <h3 className="font-heading text-lg text-charcoal mb-2">Free Breakthrough Session</h3>
                  <p className="text-sm text-charcoal-light">
                    Your first 1-hour coaching clarity session is completely free. Select "Book a Free Session" 
                    and share a little about yourself and what you're hoping to work on.
                  </p>
                </div>
                <div className="bg-gold/5 rounded-2xl p-5">
                  <h3 className="font-heading text-lg text-charcoal mb-2">Response Time</h3>
                  <p className="text-sm text-charcoal-light">
                    I personally read every message and aim to respond within 24-48 hours. 
                    Your enquiry is completely confidential.
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-3">
              {sent ? (
                <div data-testid="contact-success" className="bg-white rounded-2xl p-10 shadow-sm text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-sage/10 flex items-center justify-center">
                    <Mail className="w-10 h-10 text-sage" />
                  </div>
                  <h2 className="font-heading text-2xl text-charcoal mb-3">Message Sent!</h2>
                  <p className="text-charcoal-light">
                    Thank you for reaching out. Janelle will get back to you within 24-48 hours. 
                    In the meantime, feel free to explore the free resources.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white rounded-2xl p-8 shadow-sm space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">Your Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({...f, name: e.target.value}))}
                      required
                      data-testid="contact-name"
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sage/30"
                      placeholder="e.g. Sarah"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({...f, email: e.target.value}))}
                      required
                      data-testid="contact-email"
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sage/30"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">Subject</label>
                    <select
                      value={form.subject}
                      onChange={e => setForm(f => ({...f, subject: e.target.value}))}
                      data-testid="contact-subject"
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sage/30 bg-white"
                    >
                      {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">Your Message</label>
                    <textarea
                      value={form.message}
                      onChange={e => setForm(f => ({...f, message: e.target.value}))}
                      required
                      rows={5}
                      data-testid="contact-message"
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sage/30 resize-none"
                      placeholder="Tell me a little about yourself and how I can help..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="w-full py-3.5 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Sending...' : <>Send Message <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
